import React from "react";
import dayjs from "dayjs";
import {CrownOutlined} from "@ant-design/icons";
import {DatePicker, DatePickerSizes, Input, InputSizes, Text, TextAs, TextSizes} from "@clockout-dev/design-elements";
import ComposerSection from "./ComposerSection";
import styles from "../styles/sor-composer.module.scss";

/**
 * SOR core-owner section (manual_batch only): the core banking record the
 * full manual flow derives identity from. Uses the core-side field names
 * (`fullName`, `birthdate`, street/zip address) — deliberately different
 * casing from `identity`, mirroring the service schema.
 *
 * @param {Object} props
 * @param {Object} props.coreOwner — `sor.core_owner`
 * @param {boolean} [props.filled]
 * @param {(coreOwner: Object) => void} props.onChange
 */
function SorCoreOwnerSection({coreOwner, filled, onChange}) {
    const value = coreOwner || {};
    const setField = (field, fieldValue) => onChange({...value, [field]: fieldValue});
    const setAddressField = (field, fieldValue) => onChange({...value, address: {...(value.address || {}), [field]: fieldValue}});

    return (
        <ComposerSection icon={<CrownOutlined />} title="Core Owner (batch identity source)" filled={filled}>
            <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.sectionHint}>
                The full manual flow derives the member's identity from this core banking record — without it the batch cannot produce a derived identity.
            </Text>
            <div className={styles.fieldGrid}>
                <Input label="Full name" size={InputSizes.SMALL} value={value.fullName || ""} onChange={event => setField("fullName", event.target.value)} />
                <Input label="SSN" size={InputSizes.SMALL} placeholder="123-45-6789" value={value.ssn || ""} onChange={event => setField("ssn", event.target.value)} />
                <Input label="Email" size={InputSizes.SMALL} value={value.email || ""} onChange={event => setField("email", event.target.value)} />
                <Input label="Phone" size={InputSizes.SMALL} value={value.phone || ""} onChange={event => setField("phone", event.target.value)} />
                <DatePicker
                    label="Birthdate"
                    size={DatePickerSizes.SMALL}
                    className={styles.dateField}
                    value={value.birthdate ? dayjs(value.birthdate) : null}
                    onChange={date => setField("birthdate", date ? date.format("YYYY-MM-DD") : "")}
                />
            </div>
            <div className={styles.fieldGrid}>
                <Input label="Street" size={InputSizes.SMALL} value={value.address?.street || ""} onChange={event => setAddressField("street", event.target.value)} />
                <Input label="City" size={InputSizes.SMALL} value={value.address?.city || ""} onChange={event => setAddressField("city", event.target.value)} />
                <Input label="State" size={InputSizes.SMALL} maxLength={2} value={value.address?.state || ""} onChange={event => setAddressField("state", event.target.value.toUpperCase())} />
                <Input label="Zip" size={InputSizes.SMALL} value={value.address?.zip || ""} onChange={event => setAddressField("zip", event.target.value)} />
                <Input label="Country" size={InputSizes.SMALL} value={value.address?.country || ""} onChange={event => setAddressField("country", event.target.value)} />
            </div>
        </ComposerSection>
    );
}

export default SorCoreOwnerSection;
